/** Preferências de leitura (US-03) — tradução PURA de estado para CSS.
 *
 * Tudo aqui é função pura de propósito: aplicar preferência é escrever atributo
 * e variável no `<html>`, e uma função pura que devolve "o que escrever" é
 * testável sem montar componente nenhum. O único ponto impuro é
 * `aplicarPreferencias`, que faz as escritas — e recebe o elemento por
 * parâmetro, então o teste passa um `div`.
 *
 * ## O que cada controle realmente faz num PDF
 *
 * O conteúdo do PDF é imagem rasterizada num canvas: não há texto de HTML para
 * reestilizar. Mas o leitor em volta — sumário, painel de destaques, menus — é
 * HTML, e é ali que a maior parte dos controles vale. Daí a distinção que a UI
 * precisa deixar clara, porque ela não é óbvia para quem está lendo:
 *
 * | Controle              | Leitor (sumário, painéis) | Página do PDF               |
 * |-----------------------|---------------------------|-----------------------------|
 * | Tamanho 80–160%       | sim                       | via ZOOM (escala de render) |
 * | Família de fonte      | sim                       | impossível: glifo é vetor   |
 * | Espaçamento           | sim                       | impossível: idem            |
 * | Largura da coluna     | sim                       | limita a largura da página  |
 * | Tema / alto contraste | sim                       | `filter` NO CANVAS          |
 * | Inverter a página     | —                         | `filter` NO CANVAS          |
 * | Redução de movimento  | sim                       | —                           |
 *
 * ⚠️ Nenhum controle fica DESABILITADO por causa do formato, com uma exceção
 * (inverter a página, que só existe em PDF). Desabilitar a família de fonte em
 * PDF seria falso — o aluno disléxico quer OpenDyslexic no sumário e nos
 * próprios destaques, e é exatamente isso que o controle entrega.
 *
 * ⚠️ O filtro vai **no canvas**, nunca num ancestral: `filter` em ancestral cria
 * *containing block* para descendente `position: fixed` e — pior aqui — inverte
 * junto os destaques, os overlays e a watermark, que são camadas irmãs.
 */
import type { ReaderPreferences } from '@/types/api'

export const PADROES: ReaderPreferences = {
  font_scale: 100,
  line_spacing: 'normal',
  font_family: 'publisher',
  theme: 'light',
  high_contrast: false,
  column_width: 'normal',
  invert_pdf: false,
  tts_enabled: false,
  tts_rate: 1,
  reduce_motion: 'auto',
}

const ENTRELINHA: Record<ReaderPreferences['line_spacing'], string> = {
  compact: '1.45',
  normal: '1.7',
  wide: '2',
}

const LARGURA_COLUNA: Record<ReaderPreferences['column_width'], string> = {
  narrow: '520px',
  normal: '680px',
  wide: '880px',
}

/** `publisher` = a fonte da interface; as outras vêm auto-hospedadas (L-00b). */
const FAMILIA: Record<ReaderPreferences['font_family'], string> = {
  publisher: 'var(--fonte-interface)',
  georgia: "Georgia, 'Times New Roman', serif",
  opendyslexic: "'OpenDyslexic', var(--fonte-interface)",
}

/** O `<html>` recebe estes atributos; `tokens.css` tem um seletor para cada.
 *
 * ⚠️ Os valores são em PORTUGUÊS, e a tradução acontece aqui. O enum do
 * contrato é em inglês (`light|dark|sepia`, como `active`/`student` no resto do
 * schema), mas o CSS fala português em tudo — `data-contraste='alto'`,
 * `data-movimento='reduzido'`, `--cor-marca`. Escrever `data-tema="dark"` contra
 * um `[data-tema='escuro']` não dá erro nenhum: o seletor simplesmente não casa,
 * e o tema escuro deixa de existir para o leitor inteiro em silêncio. Foi
 * exatamente o que aconteceu, e só a captura de tela do Chromium mostrou — os
 * testes conferiam o ATRIBUTO, que estava sendo escrito corretamente.
 */
export const TEMA_CSS = {
  light: 'claro',
  dark: 'escuro',
  sepia: 'sepia',
} as const satisfies Record<ReaderPreferences['theme'], string>

export interface AtributosDoTema {
  tema: (typeof TEMA_CSS)[ReaderPreferences['theme']]
  contraste: 'normal' | 'alto'
  movimento: 'auto' | 'reduzido'
}

export function atributosDoTema(p: ReaderPreferences): AtributosDoTema {
  return {
    tema: TEMA_CSS[p.theme],
    contraste: p.high_contrast ? 'alto' : 'normal',
    movimento: p.reduce_motion === 'always' ? 'reduzido' : 'auto',
  }
}

/** Variáveis CSS derivadas — o que o chrome do leitor consome. */
export function variaveisDaLeitura(p: ReaderPreferences): Record<string, string> {
  return {
    '--escala-fonte': String(p.font_scale / 100),
    '--entrelinha': ENTRELINHA[p.line_spacing],
    '--largura-coluna': LARGURA_COLUNA[p.column_width],
    '--fonte-leitura': FAMILIA[p.font_family],
    // Título acompanha a escolha: deixar a Playfair nos cabeçalhos anularia o
    // OpenDyslexic justamente em "Capítulo 2 — Hereditariedade", que é o texto
    // que o aluno mais lê no sumário.
    '--fonte-titulo':
      p.font_family === 'publisher' ? 'var(--fonte-display)' : FAMILIA[p.font_family],
    '--filtro-pagina': filtroDaPagina(p),
  }
}

/** Filtro aplicado AO CANVAS da página do PDF.
 *
 * Inverter é decisão separada do tema porque inverter foto e mapa produz
 * negativo ilegível — e livro didático é cheio dos dois. O tema escuro liga por
 * padrão (quem o escolheu não quer uma folha branca no escuro), mas a caixa
 * "inverter as cores da página" desliga sem sair do tema.
 */
export function filtroDaPagina(p: ReaderPreferences): string {
  const partes: string[] = []
  if (p.invert_pdf) partes.push('invert(1)', 'hue-rotate(180deg)')
  if (p.high_contrast) partes.push('contrast(1.25)')
  return partes.length > 0 ? partes.join(' ') : 'none'
}

/** Fator de zoom do PDF derivado do tamanho de fonte pedido.
 *
 * O slider diz "tamanho do texto", e num PDF a única forma de aumentar o texto é
 * aumentar a página inteira. A UI explica isso na legenda — controle que não faz
 * nada visível vira chamado de suporte.
 *
 * É FATOR e não escala absoluta de propósito: multiplicado sobre a escala de
 * ajuste, o zoom continua acompanhando a largura da janela. Uma escala absoluta
 * congelaria a página e quebraria a responsividade em toda tela estreita.
 */
export function fatorDeZoomDoTexto(p: ReaderPreferences): number {
  return p.font_scale / 100
}

/** Escreve atributos e variáveis no elemento raiz. Único ponto impuro daqui. */
export function aplicarPreferencias(p: ReaderPreferences, raiz: HTMLElement): void {
  const atributos = atributosDoTema(p)
  raiz.dataset.tema = atributos.tema
  raiz.dataset.contraste = atributos.contraste
  raiz.dataset.movimento = atributos.movimento
  for (const [nome, valor] of Object.entries(variaveisDaLeitura(p))) {
    raiz.style.setProperty(nome, valor)
  }
}

/** Saneia o que veio do `localStorage` ou de um backend mais novo.
 *
 * Preferência corrompida não pode derrubar o leitor: um `theme` desconhecido
 * escreveria `data-tema="roxo"`, nenhum seletor casaria e o aluno ficaria com
 * metade do tema. Campo inválido cai no padrão, campo válido é mantido — em vez
 * de descartar o objeto inteiro por causa de um campo.
 */
export function sanearPreferencias(bruto: unknown): ReaderPreferences {
  if (typeof bruto !== 'object' || bruto === null) return { ...PADROES }
  const e = bruto as Record<string, unknown>

  const numero = (valor: unknown, min: number, max: number, padrao: number): number =>
    typeof valor === 'number' && Number.isFinite(valor) && valor >= min && valor <= max
      ? valor
      : padrao

  const daLista = <T extends string>(valor: unknown, lista: readonly T[], padrao: T): T =>
    typeof valor === 'string' && (lista as readonly string[]).includes(valor)
      ? (valor as T)
      : padrao

  const booleano = (valor: unknown, padrao: boolean): boolean =>
    typeof valor === 'boolean' ? valor : padrao

  return {
    // Passo de 10 é CHECK no banco: arredondar aqui evita 422 no primeiro PUT.
    font_scale: Math.round(numero(e.font_scale, 80, 160, PADROES.font_scale) / 10) * 10,
    line_spacing: daLista(e.line_spacing, ['compact', 'normal', 'wide'] as const, 'normal'),
    font_family: daLista(
      e.font_family,
      ['publisher', 'georgia', 'opendyslexic'] as const,
      'publisher',
    ),
    theme: daLista(e.theme, ['light', 'dark', 'sepia'] as const, 'light'),
    high_contrast: booleano(e.high_contrast, PADROES.high_contrast),
    column_width: daLista(e.column_width, ['narrow', 'normal', 'wide'] as const, 'normal'),
    invert_pdf: booleano(e.invert_pdf, PADROES.invert_pdf),
    tts_enabled: booleano(e.tts_enabled, PADROES.tts_enabled),
    tts_rate: numero(e.tts_rate, 0.5, 2, PADROES.tts_rate),
    reduce_motion: daLista(e.reduce_motion, ['auto', 'always'] as const, 'auto'),
  }
}

/** O carimbo do cliente vence o gravado? — last-write-wins (N19), lado do UI.
 *
 * Espelha o `is_newer` do backend. Sem carimbo local, o remoto vence: é o caso
 * do aluno que abre o leitor num aparelho novo.
 */
export function remotoEhMaisNovo(remoto: string | null, local: string | null): boolean {
  if (remoto === null) return false
  if (local === null) return true
  return new Date(remoto).getTime() > new Date(local).getTime()
}
